/**
 * RecordingsTab - Manage recordings and DVR
 * Refactored to use extracted components for maintainability
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Film, Eye, Plus, Youtube, RefreshCw, Link2 } from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";
import { extractDominantColor } from "../../utils/colorExtractor";

// Import extracted components
import { 
  YouTubeLinkForm, 
  GameRecordingForm, 
  RecordingListItem, 
  RecordingsStats,
  VODLinkForm
} from "./recordings";

// Initial form states
const getInitialYoutubeForm = () => ({
  youtube_url: "",
  title: "",
  sport: "Football",
  recorded_at: new Date().toISOString().split('T')[0],
  thumbnail_url: "",
  description: "",
  is_published: true
});

const getInitialGameForm = () => ({
  broadcast_title: "",
  sport: "Football",
  recorded_at: "",
  video_url: "",
  thumbnail_url: "",
  description: "",
  stats_link: "",
  home_team: { name: "", abbreviation: "", mascot: "", color: "#1a1a1a", logo_url: "" },
  away_team: { name: "", abbreviation: "", mascot: "", color: "#003087", logo_url: "" },
  final_home_score: "",
  final_away_score: "",
  is_published: true
});

export const RecordingsTab = () => {
  const [recordings, setRecordings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRecording, setEditingRecording] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", youtube_url: "", thumbnail_url: "", video_url: "" });
  const [syncingDurations, setSyncingDurations] = useState(false);
  
  // Form visibility states
  const [showAddForm, setShowAddForm] = useState(false);
  const [showYouTubeForm, setShowYouTubeForm] = useState(false);
  const [showVODForm, setShowVODForm] = useState(false);
  
  // Form data states
  const [savingGame, setSavingGame] = useState(false);
  const [savingYouTube, setSavingYouTube] = useState(false);
  const [generatingThumbnail, setGeneratingThumbnail] = useState(false);
  const [teams, setTeams] = useState([]);
  const [extractingColor, setExtractingColor] = useState({ home: false, away: false });
  
  const [youtubeForm, setYoutubeForm] = useState(getInitialYoutubeForm());
  const [gameForm, setGameForm] = useState(getInitialGameForm());

  // Fetch teams for quick select
  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data || []);
    } catch (error) { 
      console.error("Error fetching teams:", error); 
    }
  };

  const fetchRecordings = async () => {
    try {
      const [recordingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/recordings`, { headers: getAuthHeader() }),
        axios.get(`${API}/recordings/stats`, { headers: getAuthHeader() })
      ]);
      setRecordings(recordingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching recordings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchRecordings(); 
    fetchTeams();
  }, []);

  // Auto-extract color from team logo
  const handleExtractTeamColor = async (isHome) => {
    const logoUrl = isHome ? gameForm.home_team.logo_url : gameForm.away_team.logo_url;
    if (!logoUrl) return;
    
    setExtractingColor(prev => ({ ...prev, [isHome ? 'home' : 'away']: true }));
    
    try {
      const color = await extractDominantColor(logoUrl);
      if (isHome) {
        setGameForm(prev => ({ ...prev, home_team: { ...prev.home_team, color } }));
      } else {
        setGameForm(prev => ({ ...prev, away_team: { ...prev.away_team, color } }));
      }
    } catch (error) {
      console.error("Failed to extract color:", error);
    } finally {
      setExtractingColor(prev => ({ ...prev, [isHome ? 'home' : 'away']: false }));
    }
  };

  // Auto-generate thumbnail from team colors/logos
  const handleGenerateThumbnail = async () => {
    if (!gameForm.home_team.logo_url || !gameForm.away_team.logo_url) {
      alert("Please add team logos for both Home and Away teams first.");
      return;
    }
    
    setGeneratingThumbnail(true);
    try {
      const res = await axios.post(`${API}/thumbnails/generate`, {
        home_team_color: gameForm.home_team.color || "#000000",
        away_team_color: gameForm.away_team.color || "#0066CC",
        home_team_logo_url: gameForm.home_team.logo_url,
        away_team_logo_url: gameForm.away_team.logo_url,
        width: 1920,
        height: 1080
      }, { headers: getAuthHeader() });
      
      if (res.data.success && res.data.url) {
        setGameForm(prev => ({ ...prev, thumbnail_url: res.data.url }));
      } else if (res.data.preview) {
        setGameForm(prev => ({ ...prev, thumbnail_url: res.data.preview }));
      } else {
        alert("Failed to generate thumbnail");
      }
    } catch (error) {
      console.error("Error generating thumbnail:", error);
      alert("Failed to generate thumbnail. Check if logo URLs are accessible.");
    } finally {
      setGeneratingThumbnail(false);
    }
  };

  // Select team from Team Manager
  const selectTeam = (teamId, isHome) => {
    const team = teams.find(t => t.id === teamId);
    if (!team) return;
    
    const teamData = {
      name: team.name,
      abbreviation: team.abbreviation || "",
      mascot: team.mascot || "",
      color: team.primary_color || team.color || "#FFFFFF",
      logo_url: team.logo_url || ""
    };
    
    if (isHome) {
      setGameForm(prev => ({ ...prev, home_team: teamData }));
    } else {
      setGameForm(prev => ({ ...prev, away_team: teamData }));
    }
  };

  // Save new game recording
  const handleSaveGameRecording = async () => {
    if (!gameForm.video_url) {
      alert("Please enter a video URL.");
      return;
    }
    if (!gameForm.recorded_at) {
      alert("Please select a game date.");
      return;
    }
    if (!gameForm.home_team.name || !gameForm.away_team.name) {
      alert("Please enter both home and away team names.");
      return;
    }
    
    setSavingGame(true);
    try {
      const payload = {
        ...gameForm,
        final_home_score: gameForm.final_home_score ? parseInt(gameForm.final_home_score) : null,
        final_away_score: gameForm.final_away_score ? parseInt(gameForm.final_away_score) : null
      };
      
      await axios.post(`${API}/recordings/add-game`, payload, { headers: getAuthHeader() });
      
      setGameForm(getInitialGameForm());
      setShowAddForm(false);
      fetchRecordings();
      alert("Game recording added successfully!");
    } catch (error) {
      console.error("Error saving game recording:", error);
      alert("Failed to save game recording. Please try again.");
    } finally {
      setSavingGame(false);
    }
  };

  // Save YouTube Link Recording
  const handleSaveYouTubeRecording = async () => {
    if (!youtubeForm.youtube_url) {
      alert("Please enter a YouTube URL.");
      return;
    }
    if (!youtubeForm.title) {
      alert("Please enter a title.");
      return;
    }
    
    setSavingYouTube(true);
    try {
      await axios.post(`${API}/recordings/add-game`, youtubeForm, { headers: getAuthHeader() });
      
      setYoutubeForm(getInitialYoutubeForm());
      setShowYouTubeForm(false);
      fetchRecordings();
      alert("YouTube recording added successfully!");
    } catch (error) {
      console.error("Error saving YouTube recording:", error);
      alert("Failed to save YouTube recording. Please try again.");
    } finally {
      setSavingYouTube(false);
    }
  };

  // Save VOD Link Recording
  const [savingVOD, setSavingVOD] = useState(false);
  const handleSaveVODRecording = async (form) => {
    setSavingVOD(true);
    try {
      await axios.post(`${API}/recordings/add-game`, form, { headers: getAuthHeader() });
      setShowVODForm(false);
      fetchRecordings();
      alert("VOD recording added successfully!");
    } catch (error) {
      console.error("Error saving VOD recording:", error);
      alert("Failed to save VOD recording. Please try again.");
    } finally {
      setSavingVOD(false);
    }
  };

  // Sync all durations from YouTube/MP4
  const handleSyncDurations = async () => {
    if (syncingDurations) return;
    setSyncingDurations(true);
    try {
      const res = await axios.post(`${API}/recordings/sync-durations`, {}, { headers: getAuthHeader() });
      if (res.data.success) {
        if (res.data.recordings_to_sync === 0) {
          alert(res.data.message);
        } else {
          alert(`${res.data.message}\n\nThis may take a few minutes. Refresh the page to see updated durations.`);
        }
      }
    } catch (error) {
      console.error("Error syncing durations:", error);
      alert("Failed to sync durations. Please try again.");
    } finally {
      setSyncingDurations(false);
    }
  };

  const handleToggleFeatured = async (recordingId) => {
    try {
      await axios.post(`${API}/recordings/${recordingId}/feature`, {}, { headers: getAuthHeader() });
      fetchRecordings();
    } catch (error) {
      console.error("Error toggling featured:", error);
    }
  };

  const handlePublish = async (recordingId) => {
    try {
      await axios.post(`${API}/recordings/${recordingId}/publish`, {}, { headers: getAuthHeader() });
      fetchRecordings();
    } catch (error) {
      console.error("Error publishing:", error);
    }
  };

  const handleUnpublish = async (recordingId) => {
    try {
      await axios.post(`${API}/recordings/${recordingId}/unpublish`, {}, { headers: getAuthHeader() });
      fetchRecordings();
    } catch (error) {
      console.error("Error unpublishing:", error);
    }
  };

  const handleDelete = async (recordingId) => {
    if (!window.confirm("Are you sure you want to delete this recording?")) return;
    try {
      await axios.delete(`${API}/recordings/${recordingId}`, { headers: getAuthHeader() });
      fetchRecordings();
    } catch (error) {
      console.error("Error deleting recording:", error);
    }
  };

  const handleEditStart = (recording) => {
    setEditingRecording(recording.id);
    setEditForm({
      title: recording.title || "",
      youtube_url: recording.youtube_url || "",
      video_url: recording.video_url || "",
      thumbnail_url: recording.thumbnail_url || ""
    });
  };

  const handleEditSave = async (recordingId) => {
    try {
      await axios.put(`${API}/recordings/${recordingId}`, editForm, { headers: getAuthHeader() });
      setEditingRecording(null);
      fetchRecordings();
    } catch (error) {
      console.error("Error updating recording:", error);
    }
  };

  const handleEditCancel = () => {
    setEditingRecording(null);
    setEditForm({ title: "", youtube_url: "", video_url: "", thumbnail_url: "" });
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mx-auto"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">Recordings / DVR</h2>
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleSyncDurations}
            disabled={syncingDurations}
            className="bg-zinc-800 text-white text-xs px-4 py-2 h-auto rounded-none border border-white/10 hover:bg-zinc-700 disabled:opacity-50"
            data-testid="sync-durations-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncingDurations ? 'animate-spin' : ''}`} /> 
            {syncingDurations ? 'Syncing...' : 'Sync Durations'}
          </Button>
          <Link to="/watch" target="_blank">
            <Button className="bg-zinc-800 text-white text-xs px-4 py-2 h-auto rounded-none border border-white/10 hover:bg-zinc-700">
              <Eye className="w-4 h-4 mr-2" /> View On Demand
            </Button>
          </Link>
          <Button 
            onClick={() => { setShowYouTubeForm(!showYouTubeForm); setShowAddForm(false); setShowVODForm(false); }}
            className="flex items-center gap-2 bg-red-600 text-white hover:bg-red-700 font-secondary font-bold uppercase tracking-widest text-xs px-4 py-2 h-auto rounded-none"
            data-testid="add-youtube-recording-btn"
          >
            <Youtube className="w-4 h-4" /> Add YouTube Link
          </Button>
          <Button 
            onClick={() => { setShowVODForm(!showVODForm); setShowAddForm(false); setShowYouTubeForm(false); }}
            className="flex items-center gap-2 bg-cyan-600 text-white hover:bg-cyan-700 font-secondary font-bold uppercase tracking-widest text-xs px-4 py-2 h-auto rounded-none"
            data-testid="add-vod-link-btn"
          >
            <Link2 className="w-4 h-4" /> Add VOD Link
          </Button>
          <Button 
            onClick={() => { setShowAddForm(!showAddForm); setShowYouTubeForm(false); setShowVODForm(false); }}
            className="flex items-center gap-2 bg-orange-600 text-white hover:bg-orange-700 font-secondary font-bold uppercase tracking-widest text-xs px-4 py-2 h-auto rounded-none"
            data-testid="add-game-recording-btn"
          >
            <Plus className="w-4 h-4" /> Add Game Recording
          </Button>
        </div>
      </div>

      {/* YouTube Link Form */}
      {showYouTubeForm && (
        <YouTubeLinkForm
          youtubeForm={youtubeForm}
          setYoutubeForm={setYoutubeForm}
          onSave={handleSaveYouTubeRecording}
          onClose={() => setShowYouTubeForm(false)}
          saving={savingYouTube}
        />
      )}

      {/* VOD Link Form */}
      {showVODForm && (
        <VODLinkForm
          onSave={handleSaveVODRecording}
          onClose={() => setShowVODForm(false)}
          saving={savingVOD}
        />
      )}

      {/* Game Recording Form */}
      {showAddForm && (
        <GameRecordingForm
          gameForm={gameForm}
          setGameForm={setGameForm}
          teams={teams}
          onSelectTeam={selectTeam}
          onExtractColor={handleExtractTeamColor}
          extractingColor={extractingColor}
          onGenerateThumbnail={handleGenerateThumbnail}
          generatingThumbnail={generatingThumbnail}
          onSave={handleSaveGameRecording}
          onClose={() => setShowAddForm(false)}
          saving={savingGame}
        />
      )}

      {/* Stats */}
      <RecordingsStats stats={stats} />

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/30 p-4 text-blue-300 text-sm">
        <strong>Note:</strong> Recordings must be <span className="text-blue-400 font-bold">Published</span> to appear on the public On Demand page. 
        Use the globe icon to publish/unpublish recordings.
      </div>

      {/* Recordings List */}
      <div className="space-y-4">
        <h3 className="font-secondary text-lg font-bold uppercase tracking-tight text-white">All Recordings</h3>
        
        {recordings.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900/50 border border-white/5">
            <Film className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No recordings yet</p>
            <p className="text-xs text-zinc-600 mt-2">Click &ldquo;Add Game Recording&rdquo; to add your first video</p>
          </div>
        ) : (
          <div className="space-y-2">
            {recordings.map(recording => (
              <RecordingListItem
                key={recording.id}
                recording={recording}
                isEditing={editingRecording === recording.id}
                editForm={editForm}
                setEditForm={setEditForm}
                onEditStart={handleEditStart}
                onEditSave={handleEditSave}
                onEditCancel={handleEditCancel}
                onToggleFeatured={handleToggleFeatured}
                onPublish={handlePublish}
                onUnpublish={handleUnpublish}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default RecordingsTab;
